import { DownloadQueue } from "./ReportConfiguration/components/DownloadQueue";
import { useReportConfigurationSelection, useReportConfigurationSelectionPersistence } from "./ReportConfiguration/state/useReportConfigurationSelection";
import { ReportSetupEditor } from "./ReportConfiguration/components/ReportSetupEditor/ReportSetupEditor";
import { WorkbookLayoutEditor, useWorkbookEditorState } from "./ReportConfiguration/components/WorkbookLayoutEditor/WorkbookLayoutEditor";
import { MetricsEditor, useMetricsEditorState } from "./ReportConfiguration/components/MetricsEditor/MetricsEditor";
import { CategorizationEditor } from "./ReportConfiguration/components/CategorizationEditor/CategorizationEditor";
import { CalculatedFieldsEditor } from "./ReportConfiguration/components/CalculatedFieldsEditor/CalculatedFieldsEditor";
import { TripDataFiltersEditor } from "./ReportConfiguration/components/TripDataFiltersEditor/TripDataFiltersEditor";
import { InlineIcon } from "./ReportConfiguration/components/shared/EditorControls";
import { openValidationIssue as routeValidationIssue } from "./ReportConfiguration/state/validationIssueRouting";
import React, { useEffect, useState } from "react";

import { CloneReportDialog } from "./ReportConfiguration/components/CloneReportDialog";

import { GlobalTemplateConfigurator } from "./ReportConfiguration/components/GlobalTemplateConfigurator";
import { PlanEditor } from "./ReportConfiguration/components/PlanEditor";
import { PlanRail } from "./ReportConfiguration/components/PlanRail";
import { PreviewPanel, SHOW_RULE_BOOK_TAB } from "./ReportConfiguration/components/PreviewPanel";

import { ReportDatePicker } from "./ReportConfiguration/components/ReportDatePicker";
import { ReportTree } from "./ReportConfiguration/components/ReportTree";
import { SelectPicker } from "./ReportConfiguration/components/SelectPicker";

import { DestructiveConfirmDialog } from "./ReportConfiguration/components/DestructiveConfirmDialog";
import { ReportValidationDialog } from "./ReportConfiguration/components/ReportValidationDialog";
import { reportToast } from "../../components/notifications/appToast";

import {
    RULE_ACTIONS,
    RULE_OPERATORS,
    STATIC_LOOKUP_ENTITY_KEYS,
    activeTripFilterCount,
    buildRuleBook,
    buildRuleBookText,
    buildSqlPreview,
    buildExclusionSqlPreview,
    clone,
    columnMatchesKey,
    dataModelColumnLookupKey,
    dedupeLookupOptions,
    dedupeRuleNodeIds,
    findFolder,
    findPlan,
    findReport,
    findReportColumn,
    formatReportDate,
    labelLookupKey,
    lookupValuesFromOptions,
    makeEmptyConfigurationState,
    nextId,
    planPreviewText,
    scopedDataModelColumnLookupKey,
    setActiveDataModels,
    setActiveReport,
    isCalculatedColumnKey,
    setActiveLookupGroups,
    setActiveLookupOptionsByKey,
    tripDataColumnForKey,
    withBuiltInReportingPeriods,
} from "./ReportConfiguration/reportConfigurationLogic";
import type {
    BreakdownSource,
    ChangeEntry,
    CountMode,
    DataModel,
    MainTab,
    MetricSection,
    ModelKey,
    PlanConfig,
    PreviewTab,
    ReportConfig,
    ReportConfigurationLookupGroup,
    ReportConfigurationLookupOption,
    ReportConfigurationProps,
    ReportConfigurationState,
    ReportFolder,
    ReportSubTab,
} from "./ReportConfiguration/types";

import {
    validateReportConfiguration,
} from "./ReportConfiguration/reportConfigurationValidation";
import type { ReportValidationIssue } from "./ReportConfiguration/reportConfigurationValidation";
import ctcReportLogo from "../../assets/report-configuration/CallTheCar_logo.jpeg";
// @ts-ignore: side-effect import for SCSS when no type declarations are present
import "./reportConfiguration.scss";
// @ts-ignore: side-effect import for SCSS when no type declarations are present
import "./ReportConfiguration/components/workbookSheets.scss";
import { useReferenceDataLoading, useLookupLoading } from "./ReportConfiguration/state/useReferenceDataLoading";
import { useConfigurationLoading } from "./ReportConfiguration/state/useConfigurationLoading";
import { useConfigurationChangeTracking } from "./ReportConfiguration/state/useConfigurationChangeTracking";
import { useConfigurationSaveWorkflow } from "./ReportConfiguration/state/useConfigurationSaveWorkflow";
import { useExcludedRecordsDownload } from "./ReportConfiguration/state/useExcludedRecordsDownload";
import { ExcludedRecordSummary } from "./ReportConfiguration/components/shared/ExcludedRecordSummary";
import { useReportPlanManagement } from "./ReportConfiguration/state/useReportPlanManagement";

const REPORT_CONFIGURATION_SELECTION_STORAGE_KEY = "reportConfiguration.selection";
// "scheduled" and "log" are hidden until Phase 2, so a saved selection pointing at them is not restored.
const MAIN_TAB_KEYS: MainTab[] = ["plan", "reports", "globalTemplates"];
const REPORT_SUB_TAB_KEYS: ReportSubTab[] = ["setup", "categorization", "calculated", "metrics", "excel", "exclusions"];
// Kill switch for "Break Down Using -> Several Rule Sets - Combined Table" (combined
// categorizations). Set to false to hide the option for new metrics; metrics already saved
// that way keep working and keep showing it.
const COMBINED_RULE_SETS_ENABLED = true;

type PersistedReportConfigurationSelection = {
    mainTab?: MainTab;
    reportSubTab?: ReportSubTab;
    selectedPlanId?: string;
    selectedFolderId?: string;
    selectedReportId?: string;
};

function readReportConfigurationSelection(): PersistedReportConfigurationSelection {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(REPORT_CONFIGURATION_SELECTION_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        return {
            mainTab: MAIN_TAB_KEYS.includes(parsed.mainTab as MainTab) ? parsed.mainTab as MainTab : undefined,
            reportSubTab: REPORT_SUB_TAB_KEYS.includes(parsed.reportSubTab as ReportSubTab) ? parsed.reportSubTab as ReportSubTab : undefined,
            selectedPlanId: typeof parsed.selectedPlanId === "string" ? parsed.selectedPlanId : undefined,
            selectedFolderId: typeof parsed.selectedFolderId === "string" ? parsed.selectedFolderId : undefined,
            selectedReportId: typeof parsed.selectedReportId === "string" ? parsed.selectedReportId : undefined,
        };
    } catch {
        return {};
    }
}

function writeReportConfigurationSelection(selection: PersistedReportConfigurationSelection) {
    if (typeof window === "undefined" || !selection.selectedPlanId) return;
    try {
        window.localStorage.setItem(REPORT_CONFIGURATION_SELECTION_STORAGE_KEY, JSON.stringify(selection));
    } catch {
        // Ignore storage failures; selection can still fall back to the first loaded plan.
    }
}

const validationIssueSignature = (issues: ReportValidationIssue[]) =>
    issues.map((issue) => `${issue.id}:${issue.severity}:${issue.fieldKey || ""}:${issue.message}`).join("|");

const ReportConfiguration: React.FC<ReportConfigurationProps> = ({ initialData, onSave }) => {
    const [backendDataModels, setBackendDataModels] = useState<Record<ModelKey, DataModel>>({});
    const models = backendDataModels;
    // Facade consumers still need the current catalogs before editor rendering.
    setActiveDataModels(models);
    const [lookupGroups, setLookupGroups] = useState<ReportConfigurationLookupGroup[]>([]);
    const [lookupOptionsByKey, setLookupOptionsByKey] = useState<Record<string, ReportConfigurationLookupOption[]>>({});
    setActiveLookupGroups(lookupGroups);
    setActiveLookupOptionsByKey(lookupOptionsByKey);
    const [data, setData] = useState<ReportConfigurationState>(() => clone(initialData || makeEmptyConfigurationState()));
    const {
        initialSelection,
        selectedPlanId, setSelectedPlanId,
        selectedFolderId, setSelectedFolderId,
        selectedReportId, setSelectedReportId,
        mainTab, setMainTab,
        reportSubTab, setReportSubTab,
        expandedFolders, setExpandedFolders,
    } = useReportConfigurationSelection(data);
    const [reportNavigatorCollapseSignal, setReportNavigatorCollapseSignal] = useState(0);
    const [reportNavigatorOpenSignal, setReportNavigatorOpenSignal] = useState(0);
    // The Rule Book opens first: it is the view that answers "what does this report do?"
    // without the reader already knowing how the builder is organised.
    const [previewTab, setPreviewTab] = useState<PreviewTab>(SHOW_RULE_BOOK_TAB ? "ruleBook" : "summary");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [renameFolderId, setRenameFolderId] = useState<string | null>(null);
    const [renameReportId, setRenameReportId] = useState<string | null>(null);
    const [newFolderName, setNewFolderName] = useState("");
    const [newReportNames, setNewReportNames] = useState<Record<string, string>>({});
    const [modelToAdd, setModelToAdd] = useState("");
    const metricsEditorState = useMetricsEditorState();
    const [reportInvoiceNumber, setReportInvoiceNumber] = useState("");
    const [simInputs, setSimInputs] = useState<Record<string, string>>({});
    const [simRan, setSimRan] = useState(false);
    const [tripDataSearch, setTripDataSearch] = useState<Record<string, string>>({});
    const [configSaveLoading, setConfigSaveLoading] = useState(false);
    const [savedReportBaselines, setSavedReportBaselines] = useState<Record<string, { plan: PlanConfig; folder: ReportFolder; report: ReportConfig }>>({});
    const [openRuleSetId, setOpenRuleSetId] = useState("");
    const [openMetricId, setOpenMetricId] = useState("");
    const [openExcelMetricId, setOpenExcelMetricId] = useState("");
    const workbookEditorState = useWorkbookEditorState();
    const [openRuleIdsBySet, setOpenRuleIdsBySet] = useState<Record<string, string>>({});
    const [validationIssues, setValidationIssues] = useState<ReportValidationIssue[]>([]);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);

    useEffect(() => {
        document.title = "Rule Configurator";
    }, []);

    const {
        clientsLoading,
        clientsLoaded,
        clientsError,
        dataModelsLoading,
        dataModelsError,
    } = useReferenceDataLoading({
        initialSelection,
        backendDataModels,
        setBackendDataModels,
        data,
        setData,
        selectedPlanId,
        setSelectedPlanId,
        setMainTab,
        setSelectedFolderId,
        setSelectedReportId,
        setExpandedFolders,
        initialData,
    });

    const selectedPlan = findPlan(data, selectedPlanId);
    const selectedFolder = findFolder(selectedPlan, selectedFolderId);
    const selectedReport = findReport(selectedFolder, selectedReportId);
    // The edited report's calculated fields become columns of its data models for every
    // picker, preview and validation rendered below (logic/calculatedColumns.ts). Set during
    // render, before any child reads the catalog, so it always matches this render's report.
    setActiveReport(selectedReport);
    const hasPlans = data.plans.length > 0;
    const backendDataModelsReady = Object.values(models).some((model) => !!model.dataModelId);
    const lookups = lookupValuesFromOptions(lookupOptionsByKey);
    const reserveUniqueId = (prefix: string, usedIds: Set<string>) => {
        let id = nextId(prefix);
        while (usedIds.has(id)) id = nextId(prefix);
        usedIds.add(id);
        return id;
    };
    const revealAccordionItem = (id: string) => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                document.getElementById(`${id}-trigger`)?.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        });
    };

    useEffect(() => {
        setValidationIssues([]);
        setValidationDialogOpen(false);
    }, [selectedReport?.id]);

    useEffect(() => {
        if (!selectedReport) {
            setOpenRuleSetId("");
            setOpenMetricId("");
            setOpenExcelMetricId("");
            setOpenRuleIdsBySet({});
            return;
        }

        setOpenRuleSetId((current) => selectedReport.bucketRuleSets.some((set) => set.id === current) ? current : "");
        setOpenMetricId((current) => selectedReport.sections.some((section) => section.id === current) ? current : "");
        setOpenExcelMetricId((current) => selectedReport.sections.some((section) => section.id === current) ? current : "");
        setOpenRuleIdsBySet((current) => {
            const next: Record<string, string> = {};
            selectedReport.bucketRuleSets.forEach((ruleSet) => {
                const openRuleId = current[ruleSet.id];
                if (openRuleId && ruleSet.rules.some((rule) => rule.id === openRuleId)) next[ruleSet.id] = openRuleId;
            });
            return next;
        });
    }, [
        selectedReport?.id,
        selectedReport?.bucketRuleSets.map((set) => set.id).join("|"),
        selectedReport?.bucketRuleSets.map((set) => `${set.id}:${set.rules.map((rule) => rule.id).join(",")}`).join("|"),
        selectedReport?.sections.map((section) => section.id).join("|"),
    ]);

    useEffect(() => {
        setOpenRuleSetId("");
        setOpenMetricId("");
        setOpenExcelMetricId("");
        setOpenRuleIdsBySet({});
    }, [reportSubTab, selectedReport?.id]);

    const {
        reportPlanLoading,
        reportPlanLoadedForId,
        reportPlanError,
        setReportPlanError,
        configLoadLoading,
    } = useConfigurationLoading({
        clientsLoaded,
        setData,
        selectedPlanId,
        selectedFolderId,
        setSelectedFolderId,
        selectedReportId,
        setSelectedReportId,
        setExpandedFolders,
        setSavedReportBaselines,
        selectedPlan,
        selectedFolder,
        selectedReport,
        applyReportConfigurationToState: (...args) => applyReportConfigurationToState(...args),
    });

    useReportConfigurationSelectionPersistence(
        { mainTab, reportSubTab, selectedPlanId, selectedFolderId, selectedReportId },
        clientsLoaded && !reportPlanLoading && !reportPlanError && reportPlanLoadedForId === selectedPlanId,
    );

    const {
        lookupsLoading,
        lookupsError,
        lookupsLoaded,
    } = useLookupLoading({
        setLookupGroups,
        setLookupOptionsByKey,
    });
    const backendLookupsReady = lookupsLoaded;
    const saveDisabled = clientsLoading
        || (!backendDataModelsReady || dataModelsLoading || !!dataModelsError)
        || (!backendLookupsReady || lookupsLoading || !!lookupsError);

    const replacePlan = (planId: string, updater: (plan: PlanConfig) => PlanConfig) => {
        setData((current) => ({ ...current, plans: current.plans.map((plan) => plan.id === planId ? updater(plan) : plan) }));
    };

    const replaceSelectedPlan = (updater: (plan: PlanConfig) => PlanConfig) => replacePlan(selectedPlan.id, updater);

    const replaceSelectedReport = (updater: (report: ReportConfig) => ReportConfig) => {
        if (!selectedReport) return;
        replaceSelectedPlan((plan) => ({
            ...plan,
            reportFolders: plan.reportFolders.map((folder) => folder.id === selectedFolder.id
                ? { ...folder, reports: folder.reports.map((report) => report.id === selectedReport.id ? updater(report) : report) }
                : folder),
        }));
    };

    useEffect(() => {
        if (!selectedReport) return;

        const usedRuleSetIds = new Set<string>();
        const usedMetricIds = new Set<string>();
        let changed = false;

        const bucketRuleSets = selectedReport.bucketRuleSets.map((ruleSet) => {
            let ruleSetChanged = false;

            if (ruleSet.id && !usedRuleSetIds.has(ruleSet.id)) {
                usedRuleSetIds.add(ruleSet.id);
            } else {
                changed = true;
                ruleSetChanged = true;
                ruleSet = { ...ruleSet, id: reserveUniqueId("ruleset", usedRuleSetIds) };
            }

            const usedRuleIds = new Set<string>();
            const rules = ruleSet.rules.map((rule) => {
                let current = rule;
                if (current.id && !usedRuleIds.has(current.id)) {
                    usedRuleIds.add(current.id);
                } else {
                    changed = true;
                    ruleSetChanged = true;
                    current = { ...current, id: reserveUniqueId("rule", usedRuleIds) };
                }

                // Condition-tree edits address a node by id, so a duplicated id inside the
                // tree would edit or delete two nodes at once. Repair it here, same as above.
                const usedNodeIds = new Set<string>();
                const dedupedNodes = dedupeRuleNodeIds(current.conditions, usedNodeIds, (prefix) => reserveUniqueId(prefix, usedNodeIds));
                if (dedupedNodes.changed) {
                    changed = true;
                    ruleSetChanged = true;
                    current = { ...current, conditions: dedupedNodes.nodes };
                }

                return current;
            });

            return ruleSetChanged ? { ...ruleSet, rules } : ruleSet;
        });

        const sections = selectedReport.sections.map((section) => {
            if (section.id && !usedMetricIds.has(section.id)) {
                usedMetricIds.add(section.id);
                return section;
            }

            changed = true;
            return { ...section, id: reserveUniqueId("metric", usedMetricIds) };
        });

        if (changed) {
            replaceSelectedReport((current) => ({ ...current, bucketRuleSets, sections }));
        }
    }, [
        selectedReport?.id,
        selectedReport?.bucketRuleSets.map((set) => set.id).join("|"),
        selectedReport?.bucketRuleSets.map((set) => `${set.id}:${set.rules.map((rule) => rule.id).join(",")}`).join("|"),
        selectedReport?.sections.map((section) => section.id).join("|"),
    ]);

    const applyReportConfigurationToState = (
        current: ReportConfigurationState,
        targetPlanId: string,
        targetFolderId: string,
        targetReportId: string,
        nextPlan: PlanConfig,
        nextFolder: ReportFolder,
        nextReport: ReportConfig,
        changeEntry?: ChangeEntry,
    ): ReportConfigurationState => ({
        ...current,
        plans: current.plans.map((plan) => plan.id !== targetPlanId ? plan : {
            ...plan,
            logicalId: nextPlan.logicalId,
            reportFolders: plan.reportFolders.map((folder) => folder.id !== targetFolderId ? folder : {
                ...folder,
                logicalId: nextFolder.logicalId,
                reports: folder.reports.map((report) => report.id === targetReportId ? nextReport : report),
            }),
        }),
        changes: changeEntry ? [changeEntry, ...current.changes] : current.changes,
    });

    const errorMessage = (error: any, fallback: string) => {
        const responseData = error?.response?.data;
        if (typeof responseData === "string" && responseData.trim()) return responseData;
        if (typeof responseData?.message === "string" && responseData.message.trim()) return responseData.message;
        if (Array.isArray(responseData?.messages) && responseData.messages.length) {
            const message = responseData.messages[0]?.description || responseData.messages[0]?.message;
            if (message) return message;
        }
        if (error?.message) return error.message;
        return fallback;
    };

    const {
        cloneDialog,
        setCloneDialog,
        cloneLoading,
        cloneError,
        cloneFoldersLoadingClientId,
        cloneFolderLoadErrorClientId,
        cloneFolderLoadError,
        cloneSourcePlan,
        cloneSourceReport,
        openCloneReportDialog,
        loadCloneTargetFolders,
        cloneReport,
        selectPlan: commitPlanNavigation,
        addFolder,
        deleteFolder,
        addReport,
        deleteReport,
        renameFolder,
        renameReport,
    } = useReportPlanManagement({
        data,
        setData,
        selectedPlan,
        selectedFolderId,
        selectedReportId,
        setSelectedPlanId,
        setSelectedFolderId,
        setSelectedReportId,
        setExpandedFolders,
        setMainTab,
        setSavedReportBaselines,
        newFolderName,
        setNewFolderName,
        newReportNames,
        setNewReportNames,
        setReportPlanError,
        replaceSelectedPlan,
        errorMessage,
    });

    const updateMetric = (metricId: string, updater: (metric: MetricSection) => MetricSection) => {
        replaceSelectedReport((report) => ({ ...report, sections: report.sections.map((section) => section.id === metricId ? updater(section) : section) }));
    };

    const {
        pendingPlanNavigation,
        setPendingPlanNavigation,
        requestPlanNavigation,
        discardPlanNavigation,
        saveAndContinuePlanNavigation,
        pendingReportNavigation,
        setPendingReportNavigation,
        hasPendingExcelLayoutTemplateUpdate,
        hasPendingExcelLayoutTemplateCreate,
        reportBaselinePending,
        noReportChangesToSave,
        requestReportNavigation,
        discardReportNavigation,
        saveAndContinueReportNavigation,
    } = useConfigurationChangeTracking({
        plans: data.plans,
        commitPlanNavigation,
        discardCurrentReport: () => {
            const baseline = savedReportBaselines[selectedReportId];
            if (!baseline) return;
            setData((current) => applyReportConfigurationToState(
                current, selectedPlanId, selectedFolderId, selectedReportId,
                clone(baseline.plan), clone(baseline.folder), clone(baseline.report),
            ));
        },
        dataModels: models,
        lookupOptionsByKey,
        mainTab,
        selectedFolderId,
        setSelectedFolderId,
        selectedReportId,
        setSelectedReportId,
        setExpandedFolders,
        configSaveLoading,
        savedReportBaselines,
        selectedPlan,
        selectedFolder,
        selectedReport,
        lookups,
        // Keep the callback bound to this render; saving is declared below.
        saveCurrent: () => configLoadLoading || !savedReportBaselines[selectedReportId] ? Promise.resolve(false) : saveReportForNavigation(),
    });
    const saveButtonDisabled = saveDisabled || configSaveLoading || configLoadLoading || reportBaselinePending || noReportChangesToSave;
    const saveButtonTitle = saveDisabled
        ? "Backend lookup values must load before saving."
        : configLoadLoading || reportBaselinePending
            ? "Report configuration is still loading."
            : noReportChangesToSave
                ? "No changes to save."
                : undefined;
    const saveActionLabel = hasPendingExcelLayoutTemplateUpdate && !hasPendingExcelLayoutTemplateCreate
        ? "Update Template"
        : hasPendingExcelLayoutTemplateCreate && !hasPendingExcelLayoutTemplateUpdate
            ? "Save Template"
            : "Save Changes";

    const {
        downloadHistory,
        clearDownloadHistory,
        retryWorkbookDownload,
        getWorkbookDownload,
        templateRefreshToken,
        setTemplateRefreshToken,
        metricTemplateColumnId,
        saveCurrent,
        saveReportForNavigation,
        downloadWorkbook,
    } = useConfigurationSaveWorkflow({
        configurationLoading: configLoadLoading || reportPlanLoading,
        models,
        lookupOptionsByKey,
        data,
        setData,
        mainTab,
        configSaveLoading,
        setConfigSaveLoading,
        setSavedReportBaselines,
        setValidationIssues,
        setValidationDialogOpen,
        selectedPlan,
        selectedFolder,
        selectedReport,
        saveDisabled,
        lookups,
        applyReportConfigurationToState: (...args) => applyReportConfigurationToState(...args),
        errorMessage,
        reportBaselinePending,
        noReportChangesToSave,
        dataModelIdForKey: (...args) => dataModelIdForKey(...args),
        // Read these later declarations only when Save runs, as before.
        getFrequencyOptions: () => frequencyOptions,
        getReportingPeriodOptions: () => reportingPeriodOptions,
        getTripActivityOptions: () => tripActivityOptions,
        getClientOptions: () => clientOptions,
        onSave,
    });

    const { excludedRecordsLoading, excludedRecordsError, downloadExcludedRecords } = useExcludedRecordsDownload(errorMessage);

    const dataModelIdForKey = (modelKey: ModelKey) => models[modelKey]?.dataModelId;
    const modelOptions = (Object.keys(models) as ModelKey[])
        .map((key) => ({
            key,
            dataModelId: models[key].dataModelId,
            label: models[key].label,
            sortOrder: models[key].sortOrder ?? Number.MAX_SAFE_INTEGER,
            table: models[key].table,
        }))
        .filter((option) => !!option.dataModelId)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
    // Client options for a metric's Co-Plan picker. data.plans is one plan per active
    // ClientInfo row (mergeClientLookupsIntoData), so this is exactly the client list the
    // backend re-validates coPlanClientIds against at save time. Plans with no client id
    // are dropped: a Co-Plan is always a numeric ClientId in the generated SQL.
    const clientOptions: ReportConfigurationLookupOption[] = data.plans
        .filter((plan) => !!plan.clientId)
        .map((plan) => ({ id: plan.clientId as number, value: String(plan.clientId), name: plan.name || plan.clientName }));
    const ruleBook = buildRuleBook(selectedPlan, selectedReport, clientOptions);
    // previewText backs the copy button, so on the Rule Book tab it is the narrative as
    // flat text rather than SQL - the same words the panel is showing.
    const previewText = previewTab === "ruleBook"
        ? buildRuleBookText(selectedPlan, selectedReport, clientOptions)
        : previewTab === "excludedSql"
            ? buildExclusionSqlPreview(selectedPlan, selectedReport)
            : buildSqlPreview(selectedPlan, selectedReport, reportSubTab, previewTab === "technicalSql");
    const planRulePreview = planPreviewText(selectedPlan);
    const lookupOptionsForKey = (key: string) => lookupOptionsByKey[key] || [];
    const columnFromModel = (columnKey: string, modelKey?: ModelKey | "") => {
        if (modelKey && models[modelKey]) {
            return models[modelKey].categoryColumns.find((column) => columnMatchesKey(column, columnKey))
                || models[modelKey].otherColumns.find((column) => columnMatchesKey(column, columnKey))
                || models[modelKey].visibleColumns?.find((column) => columnMatchesKey(column, columnKey))
                || (modelKey === "transportation" ? tripDataColumnForKey(columnKey, models[modelKey].categoryColumns) : null)
                || null;
        }
        return null;
    };
    const lookupOptionsFromGroupsForColumn = (dataModelId?: number | null, dataModelColumnId?: number | null) => {
        if (!dataModelColumnId) return [];

        const matchingGroups = lookupGroups.filter((group) => {
            if (group.dataModelColumnId !== dataModelColumnId) return false;
            return !dataModelId || !group.dataModelId || group.dataModelId === dataModelId;
        });

        return dedupeLookupOptions(matchingGroups.flatMap((group) => group.values));
    };
    const lookupOptionsForColumn = (columnKey: string, modelKey?: ModelKey | "") => {
        // A calculated field has no lookup - only a date part's fixed values (Monday ... Sunday).
        if (isCalculatedColumnKey(columnKey)) return (selectedReport ? findReportColumn(selectedReport, columnKey)?.valueOptions : null) || [];
        const model = modelKey ? models[modelKey] : null;
        const column = columnFromModel(columnKey, modelKey);
        const optionsByColumnId = lookupOptionsFromGroupsForColumn(model?.dataModelId, column?.dataModelColumnId);
        const scopedKey = scopedDataModelColumnLookupKey(model?.dataModelId, column?.dataModelColumnId);
        const columnIdKey = dataModelColumnLookupKey(column?.dataModelColumnId);
        const labelKey = labelLookupKey(column?.label || columnKey);

        return optionsByColumnId.length ? optionsByColumnId
            : (scopedKey && lookupOptionsByKey[scopedKey]?.length ? lookupOptionsByKey[scopedKey] : null)
            || (columnIdKey && lookupOptionsByKey[columnIdKey]?.length ? lookupOptionsByKey[columnIdKey] : null)
            || (labelKey && lookupOptionsByKey[labelKey]?.length ? lookupOptionsByKey[labelKey] : null)
            || lookupOptionsForKey(columnKey);
    };
    const lookupOptionsForReportColumn = (report: ReportConfig, columnKey: string) => {
        const reportColumn = findReportColumn(report, columnKey);
        return lookupOptionsForColumn(columnKey, reportColumn?.model);
    };
    const lookupLabel = (key: string, value: string) => lookupOptionsForKey(key).find((option) => option.value === value)?.name || value;
    const lookupLabelForColumn = (columnKey: string, value: string, modelKey?: ModelKey | "") => lookupOptionsForColumn(columnKey, modelKey).find((option) => option.value === value)?.name || value;
    const lookupLabelForReportColumn = (report: ReportConfig, columnKey: string, value: string) => lookupOptionsForReportColumn(report, columnKey).find((option) => option.value === value)?.name || value;
    const lookupOptionsForEntity = (entityKey: string) => lookupOptionsByKey[entityKey] || [];
    const frequencyOptions = lookupOptionsForEntity(STATIC_LOOKUP_ENTITY_KEYS.frequency);
    // Lookup values, plus any built-in period (Last N Days / Months) the lookup table has no row for yet.
    const reportingPeriodOptions = withBuiltInReportingPeriods(lookupOptionsForEntity(STATIC_LOOKUP_ENTITY_KEYS.reportingPeriod));
    const tripActivityOptions = lookupOptionsForEntity(STATIC_LOOKUP_ENTITY_KEYS.tripActivity);
    const metricDisplayOptions = lookupOptionsForEntity(STATIC_LOOKUP_ENTITY_KEYS.metricDisplay);
    const breakdownSourceOptions: { id: string; value: BreakdownSource; name: string }[] = [
        { id: "column", value: "column", name: "Column Values" },
        { id: "ruleSet", value: "ruleSet", name: "Categorization Rule Set" },
        // Several rule sets in one table (logic/combinedRuleSets.ts). COMBINED_RULE_SETS_ENABLED is
        // the kill switch; a metric already saved this way still shows the option (MetricsEditor).
        ...(COMBINED_RULE_SETS_ENABLED ? [{ id: "ruleSets", value: "ruleSets" as BreakdownSource, name: "Several Rule Sets - Combined Table" }] : []),
    ];
    const countModeOptions: { id: string; value: CountMode; name: string }[] = [
        { id: "all", value: "all", name: "All Rows" },
        { id: "distinct", value: "distinct", name: "Distinct Values of a Column" },
    ];
    const ruleActionOptions = RULE_ACTIONS.map((action) => ({ ...action, label: lookupOptionsByKey[STATIC_LOOKUP_ENTITY_KEYS.ruleAction]?.find((option) => option.value === action.key)?.name || action.label }));
    const ruleOperatorOptions = RULE_OPERATORS.map((operator) => ({ ...operator, label: lookupOptionsByKey[STATIC_LOOKUP_ENTITY_KEYS.ruleOperator]?.find((option) => option.value === operator.key)?.name || operator.label }));
    const otpMissingHandlingOptions = lookupOptionsForEntity(STATIC_LOOKUP_ENTITY_KEYS.otpMissingHandling);
    useEffect(() => {
        if (!validationIssues.length || mainTab !== "reports" || !selectedFolder || !selectedReport) return;

        const nextIssues = validateReportConfiguration(selectedPlan, selectedFolder, selectedReport, {
            dataModels: models,
            frequencyOptions,
            reportingPeriodOptions,
            tripActivityOptions,
        });
        setValidationIssues((current) => (
            validationIssueSignature(current) === validationIssueSignature(nextIssues)
                ? current
                : nextIssues
        ));
        if (!nextIssues.some((issue) => issue.severity === "error")) {
            setValidationDialogOpen(false);
        }
    }, [
        mainTab,
        models,
        selectedFolder,
        selectedPlan,
        selectedReport,
        frequencyOptions,
        reportingPeriodOptions,
        tripActivityOptions,
        validationIssues.length,
    ]);

    const changeReportSubTab = (tab: ReportSubTab) => {
        setReportSubTab(tab);
        if (tab === "categorization" || tab === "calculated" || tab === "metrics" || tab === "excel") {
            setReportNavigatorCollapseSignal((value) => value + 1);
        }
    };
    const openValidationIssue = (issue: ReportValidationIssue) => routeValidationIssue(issue, {
        setValidationDialogOpen,
        setPendingReportNavigation,
        setMainTab,
        setReportSubTab,
        setOpenRuleSetId,
        setOpenRuleIdsBySet,
        setOpenMetricId,
        setOpenExcelMetricId,
    });
    const changeMainTab = (tab: MainTab) => {
        setMainTab(tab);
        if (tab === "reports") {
            setReportNavigatorOpenSignal((value) => value + 1);
        }
    };

    useEffect(() => {
        const targets = Array.from(document.querySelectorAll<HTMLElement>("[data-rc-validation]"));
        targets.forEach((target) => {
            target.classList.remove("rc-validation-target-error", "rc-validation-target-warning");
            target.removeAttribute("data-rc-validation-severity");
        });

        const severityByField = new Map<string, ReportValidationIssue["severity"]>();
        validationIssues.forEach((issue) => {
            if (!issue.fieldKey) return;
            const current = severityByField.get(issue.fieldKey);
            if (current === "error") return;
            severityByField.set(issue.fieldKey, issue.severity);
        });

        targets.forEach((target) => {
            const fieldKey = target.getAttribute("data-rc-validation");
            if (!fieldKey) return;
            const severity = severityByField.get(fieldKey);
            if (!severity) return;
            target.classList.add(severity === "error" ? "rc-validation-target-error" : "rc-validation-target-warning");
            target.setAttribute("data-rc-validation-severity", severity);
        });
    }, [
        validationIssues,
        reportSubTab,
        openRuleSetId,
        openMetricId,
        openExcelMetricId,
        openRuleIdsBySet,
    ]);

    return (
        <div className="rc-page">
            <div className="rc-page-header">
                <h1>Report Configuration</h1>
            </div>

            <div className={previewOpen ? "rc-shell preview-open" : "rc-shell preview-closed"}>
                <PlanRail
                    hasPlans={hasPlans}
                    data={data}
                    selectedPlan={selectedPlan}
                    clientsLoading={clientsLoading}
                    clientsError={clientsError}
                    onSelectPlan={requestPlanNavigation}
                />

                <main className="rc-main">
                    <>
                        <div className="rc-tabbar rc-main-tabs">
                            {[
                                // Temporarily hidden. Keep the Plan editor/code available for future plan-level configuration.
                                // ["plan", "Plan Configuration", "tune"],
                                ["reports", "Reports", "article"],
                                ["globalTemplates", "Global Templates", "dashboard_customize"],
                                // Hidden until Phase 2 - uncomment these (and add "scheduled"/"log" back to
                                // MAIN_TAB_KEYS here and MAIN_TABS in useReportConfigurationSelection.ts) to restore.
                                // ["scheduled", "Scheduled Changes", "event"],
                                // ["log", "Change Log", "history"],
                            ].map(([key, label, icon]) => (
                                <button key={key} className={mainTab === key ? "rc-tab active" : "rc-tab"} onClick={() => changeMainTab(key as MainTab)} disabled={!hasPlans && key !== "globalTemplates"}>
                                    <span className="material-icons md-16" aria-hidden="true">{icon}</span>
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>
                        {dataModelsLoading && <p className="rc-hint">Loading Data Models...</p>}
                        {dataModelsError && <p className="rc-hint">{dataModelsError}</p>}
                        {hasPlans && lookupsLoading && <p className="rc-hint">Loading Report Lookup Values...</p>}
                        {hasPlans && lookupsError && <p className="rc-hint">{lookupsError}</p>}
                        {hasPlans && reportPlanLoading && <p className="rc-hint">Loading Report Folders and Reports...</p>}
                        {hasPlans && reportPlanError && <p className="rc-hint">{reportPlanError}</p>}
                        {mainTab !== "globalTemplates" && saveDisabled && !clientsLoading && !dataModelsLoading && !lookupsLoading && <p className="rc-hint">Save is disabled until backend data models and report lookup values load.</p>}

                        {mainTab === "globalTemplates" && renderGlobalTemplates()}
                        {hasPlans && mainTab === "plan" && renderPlanEditor()}
                        {hasPlans && mainTab === "reports" && renderReportsEditor()}
                        {hasPlans && mainTab === "scheduled" && renderScheduled()}
                        {hasPlans && mainTab === "log" && renderLog()}
                        {!hasPlans && mainTab !== "globalTemplates" && (
                        <div className="rc-block">
                            <h3>{clientsLoading ? "Loading Health Plans" : "No Health Plans Available"}</h3>
                            <p className="rc-hint">Health plans will appear here after active ClientInfo records are returned.</p>
                        </div>
                        )}
                    </>
                </main>

                <PreviewPanel
                    downloadQueue={<DownloadQueue entries={downloadHistory} onClear={clearDownloadHistory} onRetry={retryWorkbookDownload} />}
                    hasPlans={hasPlans && mainTab !== "globalTemplates"}
                    previewOpen={previewOpen}
                    mainTab={mainTab}
                    plan={selectedPlan}
                    report={selectedReport}
                    dataModels={models}
                    clientOptions={clientOptions}
                    ruleBook={ruleBook}
                    planRulePreview={planRulePreview}
                    previewTab={previewTab}
                    previewText={previewText}
                    onClose={() => setPreviewOpen(false)}
                    onOpen={() => setPreviewOpen(true)}
                    onPreviewTabChange={setPreviewTab}
                />
                <DestructiveConfirmDialog
                    open={!!pendingReportNavigation}
                    kind="report"
                    title="Unsaved report changes"
                    kicker="Review before switching"
                    subject={selectedReport?.label || "Current report"}
                    subjectIcon="edit_note"
                    visualIcon="published_with_changes"
                    context={pendingReportNavigation ? `Switching to ${pendingReportNavigation.folderLabel} / ${pendingReportNavigation.reportLabel}` : undefined}
                    description="This report has changes that have not been saved yet. Choose how you want to continue."
                    impact={[
                        "Save and Continue saves this report first, then opens the selected report.",
                        "Discard Changes opens the selected report without saving these edits.",
                    ]}
                    loading={configSaveLoading}
                    confirmTone="primary"
                    confirmIcon="save"
                    confirmLabel="Save and Continue"
                    loadingLabel="Saving..."
                    secondaryActionLabel="Discard Changes"
                    secondaryActionIcon="restart_alt"
                    onSecondaryAction={discardReportNavigation}
                    cancelLabel="Keep Editing"
                    onCancel={() => !configSaveLoading && setPendingReportNavigation(null)}
                    onConfirm={saveAndContinueReportNavigation}
                />
                <DestructiveConfirmDialog
                    open={!!pendingPlanNavigation}
                    kind="report"
                    title="Unsaved report changes"
                    kicker="Review before switching"
                    subject={selectedReport?.label || "Current report"}
                    subjectIcon="edit_note"
                    visualIcon="published_with_changes"
                    context={pendingPlanNavigation ? `Switching to ${pendingPlanNavigation.planLabel}` : undefined}
                    description="This report has changes that have not been saved yet. Choose how you want to continue."
                    impact={[
                        "Save and Continue saves this report first, then opens the selected health plan.",
                        "Discard Changes opens the selected health plan without saving these edits.",
                    ]}
                    loading={configSaveLoading}
                    confirmTone="primary"
                    confirmIcon="save"
                    confirmLabel="Save and Continue"
                    loadingLabel="Saving..."
                    secondaryActionLabel="Discard Changes"
                    secondaryActionIcon="restart_alt"
                    onSecondaryAction={discardPlanNavigation}
                    cancelLabel="Keep Editing"
                    onCancel={() => !configSaveLoading && setPendingPlanNavigation(null)}
                    onConfirm={saveAndContinuePlanNavigation}
                />
                <CloneReportDialog
                    open={!!cloneDialog}
                    plans={data.plans}
                    sourcePlan={cloneSourcePlan}
                    sourceReport={cloneSourceReport}
                    loading={cloneLoading}
                    loadingFoldersClientId={cloneFoldersLoadingClientId}
                    folderLoadErrorClientId={cloneFolderLoadErrorClientId}
                    folderLoadError={cloneFolderLoadError}
                    error={cloneError}
                    onLoadFolders={loadCloneTargetFolders}
                    onCancel={() => !cloneLoading && setCloneDialog(null)}
                    onClone={cloneReport}
                />
                <ReportValidationDialog
                    open={validationDialogOpen}
                    issues={validationIssues}
                    onClose={() => setValidationDialogOpen(false)}
                    onOpenIssue={openValidationIssue}
                />
            </div>
        </div>
    );

    function renderPlanEditor() {
        return (
            <PlanEditor
                selectedPlan={selectedPlan}
            />
        );
    }

    function renderGlobalTemplates() {
        return (
            <GlobalTemplateConfigurator
                models={models}
                dataModelsLoading={dataModelsLoading}
                dataModelsError={dataModelsError}
                onTemplateSaved={() => setTemplateRefreshToken((current) => current + 1)}
            />
        );
    }

    function renderReportsEditor() {
        const canAddFolder = !!selectedPlan.reportPlanId && !reportPlanLoading;
        const addFolderDisabledReason = reportPlanLoading
            ? "Report plan metadata is still loading - please wait a moment."
            : !selectedPlan.reportPlanId
                ? "Report plan metadata has not loaded yet for this health plan."
                : undefined;
        return (
            <ReportTree
                selectedPlan={selectedPlan}
                selectedFolder={selectedFolder}
                selectedReport={selectedReport}
                expandedFolders={expandedFolders}
                setExpandedFolders={setExpandedFolders}
                renameFolderId={renameFolderId}
                setRenameFolderId={setRenameFolderId}
                renameReportId={renameReportId}
                setRenameReportId={setRenameReportId}
                newReportNames={newReportNames}
                setNewReportNames={setNewReportNames}
                newFolderName={newFolderName}
                setNewFolderName={setNewFolderName}
                replaceSelectedPlan={replaceSelectedPlan}
                deleteFolder={deleteFolder}
                deleteReport={deleteReport}
                addReport={addReport}
                addFolder={addFolder}
                canAddFolder={canAddFolder}
                addFolderDisabledReason={addFolderDisabledReason}
                renameFolder={renameFolder}
                renameReport={renameReport}
                requestReportNavigation={requestReportNavigation}
                collapseNavigatorSignal={reportNavigatorCollapseSignal}
                openNavigatorSignal={reportNavigatorOpenSignal}
                renderReportEditor={renderReportEditor}
            />
        );
    }

    function renderReportEditor(report: ReportConfig) {
        const visibleSubtabs: { key: ReportSubTab; label: string }[] = [
            { key: "setup", label: "Setup" },
            { key: "categorization", label: `Categorization Rules${report.bucketRuleSets.length ? ` (${report.bucketRuleSets.length})` : ""}` },
            { key: "calculated", label: `Calculated Fields${report.calculatedFields?.length ? ` (${report.calculatedFields.length})` : ""}` },
            { key: "metrics", label: `Metrics${report.sections.length ? ` (${report.sections.length})` : ""}` },
            { key: "excel", label: "Excel Layout" },
            { key: "exclusions", label: "Excluded Records Summary" },
            //...(report.models.includes("transportation") ? [{ key: "tripdata" as ReportSubTab, label: `Trip Data & Exclusions${activeTripFilterCount(report) ? ` (${activeTripFilterCount(report)})` : ""}` }] : []),
        ];
        const workbookDownload = getWorkbookDownload(report);
        const workbookLoading = !!workbookDownload && !workbookDownload.finishedAt;
        const workbookError = workbookDownload?.error;
        const workbookButtonLabel = workbookDownload?.stage === "queued" || workbookDownload?.stage === "ready" ? "Queued..."
            : workbookDownload?.stage === "submitting" ? "Queueing..."
            : workbookDownload?.stage === "downloading" ? "Downloading..." : "Generating...";
        const activeSubTab = visibleSubtabs.some((tab) => tab.key === reportSubTab) ? reportSubTab : "setup";
        const issueSummaryForTab = (tab: ReportSubTab) => {
            const tabIssues = validationIssues.filter((issue) => issue.tab === tab);
            const errors = tabIssues.filter((issue) => issue.severity === "error").length;
            const warnings = tabIssues.length - errors;
            return { errors, warnings, total: tabIssues.length };
        };
        const renderSubtabLabel = (tab: { key: ReportSubTab; label: string }) => {
            const summary = issueSummaryForTab(tab.key);
            return (
                <>
                    <span className="rc-tab-label">{tab.label}</span>
                    {summary.total > 0 && (
                        <span
                            className={summary.errors ? "rc-tab-issue-badge error" : "rc-tab-issue-badge warning"}
                            title={[
                                summary.errors ? `${summary.errors} error${summary.errors === 1 ? "" : "s"}` : "",
                                summary.warnings ? `${summary.warnings} warning${summary.warnings === 1 ? "" : "s"}` : "",
                            ].filter(Boolean).join(", ")}
                        >
                            {summary.errors || summary.warnings}
                        </span>
                    )}
                </>
            );
        };

        return (
            <>
                <section className="rc-block">
                    <h3><span data-rc-validation="folder-name">{selectedFolder.label}</span> <InlineIcon icon="chevron_right" label="to" /> <span data-rc-validation="report-name">{report.label}</span></h3>
                    <div className="rc-current">Currently Active: {formatReportDate(report.effectiveStart)} <InlineIcon icon="east" label="to" /> Ongoing</div>
                    <div className="rc-inline-fields">
                        <label data-rc-validation="report-frequency"><span>How Often This Report Runs</span><SelectPicker options={frequencyOptions} value={report.frequency} onChange={(value) => replaceSelectedReport((current) => ({ ...current, frequency: value }))} searchPlaceholder="Search Frequency..." /></label>
                        <label data-rc-validation="effective-start"><span>Effective Start Date</span><ReportDatePicker value={report.effectiveStart} onChange={(value) => replaceSelectedReport((current) => ({ ...current, effectiveStart: value }))} /></label>
                        <label data-rc-validation="effective-end"><span>Effective End Date (Optional)</span><ReportDatePicker value={report.effectiveEnd} onChange={(value) => replaceSelectedReport((current) => ({ ...current, effectiveEnd: value }))} /></label>
                    </div>
                </section>
                <div className="rc-tabbar subtabs">
                    {visibleSubtabs.map((tab) => <button key={tab.key} className={activeSubTab === tab.key ? "rc-tab active" : "rc-tab"} onClick={() => changeReportSubTab(tab.key)}>{renderSubtabLabel(tab)}</button>)}
                    <span className="rc-tabbar-spacer" />
                    <button type="button" className="rc-action-button rc-action-secondary rc-tab-clone-action" disabled={!report.reportId || cloneLoading} title={!report.reportId ? "Report metadata has not loaded yet." : "Clone report"} onClick={() => selectedFolder && openCloneReportDialog(selectedFolder, report)}>
                        <span className="material-icons md-18" aria-hidden="true">{cloneLoading ? "sync" : "content_copy"}</span>
                        <span>{cloneLoading ? "Cloning..." : "Clone Report"}</span>
                    </button>
                </div>
                <ExcludedRecordSummary key={report.id} source={{ reportId: report.reportId || 0, configVersionId: report.configVersionId }}
                    active={activeSubTab === "exclusions"} ready={!configLoadLoading && !reportBaselinePending}
                    hasUnsavedChanges={!noReportChangesToSave} refreshKey={savedReportBaselines[report.id]?.report} />
                {activeSubTab === "setup" && <ReportSetupEditor report={report}
                    catalog={{ modelOptions, reportingPeriodOptions, models, dataModelIdForKey, dataModelsLoading, dataModelsError }}
                    session={{ modelToAdd, setModelToAdd, reportInvoiceNumber, setReportInvoiceNumber }}
                    editing={{ replaceSelectedReport }}
                />}
                {activeSubTab === "categorization" && <CategorizationEditor report={report}
                    selection={{ openRuleSetId, setOpenRuleSetId, openRuleIdsBySet, setOpenRuleIdsBySet }}
                    editing={{ reserveUniqueId, replaceSelectedReport, revealAccordionItem }}
                    catalog={{ lookupOptionsForReportColumn, ruleActionOptions, lookupLabelForReportColumn }}
                    session={{ simInputs, setSimInputs, setSimRan, simRan }}
                />}
                {activeSubTab === "calculated" && <CalculatedFieldsEditor report={report}
                    editing={{ replaceSelectedReport, revealAccordionItem }}
                />}
                {activeSubTab === "metrics" && <MetricsEditor report={report}
                    plan={selectedPlan}
                    catalog={{ metricDisplayOptions, models, breakdownSourceOptions, countModeOptions, lookupOptionsForColumn, lookupLabelForColumn, otpMissingHandlingOptions, lookupOptionsForReportColumn, lookupLabelForReportColumn, clientOptions }}
                    selection={{ openMetricId, setOpenMetricId, setOpenExcelMetricId }}
                    editing={{
                        updateMetric,
                        reserveUniqueId,
                        replaceSelectedReport,
                        revealAccordionItem,
                        openRuleSet: (ruleSetId: string) => {
                            changeReportSubTab("categorization");
                            setOpenRuleSetId(ruleSetId);
                            revealAccordionItem(`ruleset-${ruleSetId}`);
                        },
                    }}
                    session={metricsEditorState}
                />}
                {activeSubTab === "excel" && <WorkbookLayoutEditor report={report}
                    session={workbookEditorState}
                    selectedPlan={selectedPlan}
                    catalog={{ reportingPeriodOptions, models, dataModelIdForKey, metricTemplateColumnId, templateRefreshToken }}
                    selection={{ openExcelMetricId, setOpenExcelMetricId }}
                    editing={{ replaceSelectedReport, updateMetric }}
                    validationIssues={validationIssues}
                />}
                {activeSubTab === "tripdata" && <TripDataFiltersEditor report={report}
                    editing={{ replaceSelectedReport }}
                    catalog={{ lookupOptionsForColumn }}
                    session={{ tripDataSearch, setTripDataSearch }}
                />}
                <section className="rc-block rc-actions rc-report-actions">
                    <div className="rc-report-actions-group">
                        <button type="button" className="rc-action-button rc-action-secondary" disabled={!report.reportId || workbookLoading || configLoadLoading || configSaveLoading || reportBaselinePending} title={!report.reportId ? "Save the report before downloading." : configLoadLoading || reportBaselinePending ? "Report configuration is still loading." : configSaveLoading ? "Report configuration is being saved." : !noReportChangesToSave ? "Downloads the saved configuration. Save your changes to include them in the download." : undefined} onClick={() => downloadWorkbook(report, selectedPlan.name)}>
                            <span className="material-icons md-18" aria-hidden="true">{workbookLoading ? "sync" : "file_download"}</span>
                            <span>{workbookLoading ? workbookButtonLabel : "Download Report"}</span>
                        </button>
                        <button type="button" className="rc-action-button rc-action-secondary" disabled={!report.reportId || excludedRecordsLoading || configLoadLoading} title={!report.reportId ? "Save the report before downloading." : configLoadLoading ? "Report configuration is still loading." : "Download the records each metric left out (same reporting period and health plan), with the reason for each."} onClick={() => downloadExcludedRecords(report)}>
                            <span className="material-icons md-18" aria-hidden="true">{excludedRecordsLoading ? "sync" : "file_download"}</span>
                            <span>{excludedRecordsLoading ? "Generating..." : "Download Excluded Records"}</span>
                        </button>
                        <button type="button" className="primary rc-action-button rc-action-primary" disabled={saveButtonDisabled} title={saveButtonTitle} onClick={saveCurrent}>
                            <span className="material-icons md-18" aria-hidden="true">{configSaveLoading ? "sync" : "save"}</span>
                            <span>{configSaveLoading ? "Saving..." : configLoadLoading ? "Loading..." : saveActionLabel}</span>
                        </button>
                    </div>
                    {workbookError && <p className="rc-hint">{workbookError}</p>}
                    {excludedRecordsError && <p className="rc-hint">{excludedRecordsError}</p>}
                </section>
            </>
        );
    }

    function renderScheduled() {
        const rows = data.changes.filter((entry) => entry.status === "Scheduled");
        if (!rows.length) return <p className="rc-hint">No Scheduled Changes For {selectedPlan.name}.</p>;
        return <>{rows.map((entry) => <section className="rc-block" key={entry.id}><h3><span className="rc-badge">{entry.level}</span> <EntryLabel label={entry.label} /> <span className="rc-badge-scheduled">Scheduled</span></h3><p className="rc-block-hint">Starts {formatReportDate(entry.start)}, ends {formatReportDate(entry.end)}. Saved by {entry.user}.</p><ul>{entry.changes.map((change) => <li key={change}>{change}</li>)}</ul><button onClick={() => setData((current) => ({ ...current, changes: current.changes.filter((item) => item.id !== entry.id) }))}>Cancel This Scheduled Change</button></section>)}</>;
    }

    function renderLog() {
        if (!data.changes.length) return <p className="rc-hint">No History Yet For {selectedPlan.name}.</p>;
        return <section className="rc-block">{data.changes.map((entry) => <div className="rc-changerow" key={entry.id}><div className="rc-log-top"><span><span className="rc-badge">{entry.level}</span> <b><EntryLabel label={entry.label} /></b></span><span className={entry.status === "Active" ? "rc-badge-active" : entry.status === "Scheduled" ? "rc-badge-scheduled" : "rc-badge-past"}>{entry.status}</span></div><div className="rc-log-meta">{formatReportDate(entry.start)} <InlineIcon icon="east" label="to" /> {formatReportDate(entry.end)} - {entry.user} - {entry.ts}</div>{entry.changes.length ? <ul>{entry.changes.map((change) => <li key={change}>{change}</li>)}</ul> : <p className="rc-hint">Initial Configuration, Nothing To Compare Against.</p>}</div>)}</section>;
    }
};

function EntryLabel({ label }: { label: string }) {
    const legacySeparator = ` ${String.fromCharCode(45)}${String.fromCharCode(62)} `;
    const separator = label.includes("|") ? "|" : legacySeparator;
    const parts = label.split(separator);

    if (parts.length < 2) return <>{label}</>;

    return <>{parts[0]} <InlineIcon icon="chevron_right" label="to" /> {parts.slice(1).join(" ")}</>;
}

export default ReportConfiguration;
